import { ArrowUpRight, ArrowDownRight, Minus, ChevronRight, Target } from 'lucide-react';
import ExplainVarianceBubble from './ExplainVarianceBubble';

const formatValue = (value, format, unit) => {
  if (value === null || value === undefined || Number.isNaN(value)) return '—';
  if (format === 'currency') {
    return new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP', maximumFractionDigits: 2 }).format(value);
  }
  if (format === 'percent') return `${value.toFixed(1)}%`;
  if (format === 'int') return new Intl.NumberFormat('en-GB').format(Math.round(value));
  return String(value) + (unit || '');
};

// Tiles for which we automatically surface an AI insight CTA.
const INSIGHTABLE = new Set(['profit', 'labour_pct', 'food_gp_pct', 'drink_gp_pct', 'wastage_pct', 'revenue_vs_forecast', 'forecast_variance']);

// A card is wide only when it has a chart to fill it (Chris's review L21): the wide ones held a
// single number, and read as a rendering fault. None do yet.
const SPAN_MAP = {};

const KpiCard = ({ kpi, range, siteLabel, context, budget, onExpand, compareLabel = 'previous' }) => {
  const delta = kpi.delta_pct;
  const isGood = (() => {
    if (delta === null || delta === undefined) return null;
    const lowerIsBetter = ['labour_pct', 'wastage_pct'].includes(kpi.key);
    if (lowerIsBetter) return delta < 0;
    return delta > 0;
  })();

  const deltaColor = isGood === null ? 'text-ink-faint' : isGood ? 'text-signal-ok' : 'text-signal-stop';
  const DeltaIcon = delta === null || delta === undefined ? Minus : delta > 0 ? ArrowUpRight : delta < 0 ? ArrowDownRight : Minus;
  const span = SPAN_MAP[kpi.key] || '';

  // Optional target comparison for percent cards
  const targetHint = (() => {
    if (kpi.target === null || kpi.target === undefined) return null;
    const diff = kpi.value - kpi.target;
    const lowerIsBetter = ['labour_pct', 'wastage_pct'].includes(kpi.key);
    const good = lowerIsBetter ? diff <= 0 : diff >= 0;
    return (
      // Formatted like the figure it's a target for (Chris's review M3), and met or missed in
      // words as well as colour (L23).
      <span className={`text-[11px] uppercase tracking-wider font-semibold ${good ? 'text-signal-ok' : 'text-signal-warn'}`}>
        Target {formatValue(kpi.target, kpi.format, kpi.unit)} · {good ? 'met' : 'missed'}
      </span>
    );
  })();

  // Budget overlay pill — present when a budget exists for this KPI in the current month
  const budgetPill = (() => {
    if (!budget || budget.monthly_target === null || budget.monthly_target === undefined) return null;
    const toneByStatus = {
      good: 'bg-signal-ok-tint text-signal-ok border-signal-ok-line',
      warn: 'bg-signal-warn-tint text-signal-warn border-signal-warn-line',
      bad:  'bg-signal-stop-tint text-signal-stop border-signal-stop-line',
    };
    const tone = toneByStatus[budget.status] || 'bg-surface-2 text-ink-soft border-hairline';
    const fmtTarget = () => {
      if (kpi.format === 'currency') return `£${Math.round(budget.monthly_target).toLocaleString('en-GB')}`;
      if (kpi.format === 'percent') return `${budget.monthly_target}%`;
      return Math.round(budget.monthly_target).toLocaleString('en-GB');
    };
    // Early in the month, the prorated variance % can look alarming (e.g. +732% on day 1).
    // Suppress the % suffix when the month is <8% elapsed AND the KPI is cumulative
    // (anything except percent KPIs which are absolute).
    const isCumulative = kpi.format !== 'percent';
    const monthProgress = budget.month_progress_pct ?? null;
    const suppressPct = isCumulative && monthProgress !== null && monthProgress < 8;
    return (
      <span
        className={`inline-flex items-center gap-1 text-[12.5px] font-semibold px-1.5 py-0.5 rounded-md border ${tone}`}
        title={`Monthly target ${fmtTarget()}${budget.variance_pct !== null ? ` · ${budget.variance_pct > 0 ? '+' : ''}${budget.variance_pct}% to budget` : ''}`}
        data-testid={`kpi-budget-pill-${kpi.key}`}
      >
        <Target size={9} />
        {fmtTarget()}
        {budget.variance_pct !== null && !suppressPct && (
          <span className="opacity-70">· {budget.variance_pct > 0 ? '+' : ''}{budget.variance_pct}%</span>
        )}
      </span>
    );
  })();

  const isInsightable = INSIGHTABLE.has(kpi.key);

  return (
    <div
      className={`relative bg-white border border-hairline rounded-lg p-5 flex flex-col ${span}`}
      data-testid={`kpi-card-${kpi.key}`}
    >
      <div className="flex items-start justify-between mb-2 gap-2">
        <span className="font-heading text-[11px] font-bold uppercase tracking-[0.06em] text-ink-faint">{kpi.label}</span>
        <div className="flex items-center gap-1.5 flex-wrap justify-end">
          {budgetPill}
          {targetHint}
        </div>
      </div>

      <div className="flex items-baseline gap-3 flex-wrap">
        <span className="font-mono tabular-nums text-[30px] leading-none text-ink" data-testid={`kpi-value-${kpi.key}`}>
          {formatValue(kpi.value, kpi.format, kpi.unit)}
        </span>
        {delta !== null && delta !== undefined && (
          <span className={`inline-flex items-center gap-0.5 text-xs font-semibold ${deltaColor}`}>
            <DeltaIcon size={13} />
            {Math.abs(delta).toFixed(1)}%
          </span>
        )}
      </div>

      {/* Set against the stretch before, named ("vs £812 yesterday"); with nothing then, it
          says so rather than "vs £0.00", which read as growth from nothing (Chris's review M2). */}
      {kpi.previous !== null && kpi.previous !== undefined && (
        <p className="text-[12.5px] text-ink-faint mt-1">
          {kpi.compare_traded === false
            ? `No figures for ${compareLabel}`
            : `vs ${formatValue(kpi.previous, kpi.format, kpi.unit)} ${compareLabel}`}
        </p>
      )}

      {/* Spacer pushes the action zone to the bottom */}
      <div className="flex-1" />

      {/* Explain Variance — only when applicable, sits above See more info */}
      {isInsightable && (
        <div className="mt-4">
          <ExplainVarianceBubble kpi={kpi} range={range} siteLabel={siteLabel} context={context} />
        </div>
      )}

      {/* See more info — always in the same bottom-right position on every card */}
      <div className="mt-3 pt-3 border-t border-hairline flex justify-end">
        <button
          onClick={() => onExpand && onExpand(kpi)}
          className="text-[12.5px] font-semibold text-verlo hover:text-verlo-hover flex items-center gap-0.5 transition-colors"
          data-testid={`kpi-expand-${kpi.key}`}
        >
          {/* Says what it's more of (Chris's review L7). */}
          More on {String(kpi.label || '').toLowerCase()}
          <ChevronRight size={12} />
        </button>
      </div>
    </div>
  );
};

export default KpiCard;
